import React, { useState } from 'react';
import { supabase, isConfigured } from '../services/supabase';
import { Mail, Loader2, ArrowRight, CheckCircle2, AlertCircle, X, Check, Coins, Chrome } from 'lucide-react';
import { APP_NAME } from '../constants';
import { HOSTING_PLANS, TOUR_SUBSCRIPTION_TIERS } from '../types';

interface LoginProps {
  onClose?: () => void;
  initialMode?: 'login' | 'signup';
}

const Login: React.FC<LoginProps> = ({ onClose, initialMode = 'login' }) => {
  const [mode, setMode] = useState<'login' | 'signup'>(initialMode);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedMarketing, setAcceptedMarketing] = useState(false);
  const [website, setWebsite] = useState('');

  // Capture referral code from URL
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const refCode = params.get('ref');
    if (refCode) { localStorage.setItem('referral_code', refCode);
      try {
        document.cookie = `rehub_referral_code=${encodeURIComponent(refCode)}; path=/; max-age=${60 * 60 * 24 * 14}; SameSite=Lax`;
      } catch {} }
  }, []);

  const handleGoogleSignIn = async () => {
    if (!acceptedTerms) {
      setError("Please accept the Terms and Conditions to continue.");
      return;
    }
    if (!isConfigured) {
      setError("System Error: Supabase connection not configured.");
      return;
    }
    setGoogleLoading(true);
    setError(null);
    try {
      if (mode === 'signup' && acceptedMarketing) {
        localStorage.setItem('pending_marketing_consent', 'true');
      }
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          queryParams: { access_type: 'offline', prompt: 'consent' },
        },
      });
      if (error) throw error;
    } catch (err: any) {
      console.error('Google Sign-In Error:', err);
      setError(err.message || 'Failed to sign in with Google');
      setGoogleLoading(false);
    }
  };

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (!acceptedTerms) { setError('Please accept the Terms and Conditions to continue.'); setLoading(false); return; }
    if (website.trim()) { setError('Something went wrong. Please try again.'); setLoading(false); return; }
    if (!isConfigured) { setError('System Error: Supabase connection not configured.'); setLoading(false); return; }

    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: { emailRedirectTo: `${window.location.origin}/auth/callback`, data: { signup_app: 'planos-studio' } },
      });
      if (error) throw error;
      if (mode === 'signup') localStorage.setItem('pending_marketing_consent', acceptedMarketing ? 'true' : 'false');
      setSent(true);
    } catch (err: any) {
      if (err.message === 'Failed to fetch' || !navigator.onLine) setError('Connection failed. Please check your internet.');
      else if (err.status === 400) setError('Invalid email address or request.');
      else setError(err.message || 'Failed to send login link');
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = () => { setMode(mode === 'login' ? 'signup' : 'login'); setError(null); setSent(false); setAcceptedTerms(false); setAcceptedMarketing(false); setWebsite(''); };

  if (sent) {
    return (
      <div className="flex flex-col items-center justify-center p-4 sm:p-6 w-full min-h-0">
        <div className="max-w-md w-full border rounded-2xl p-6 sm:p-8 text-center shadow-2xl relative" style={{ backgroundColor: '#2F2F2F', borderColor: '#3d3d3d' }}>
          {onClose && <button onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-white transition-colors"><X size={24} /></button>}
          <div className="w-16 h-16 bg-green-900/30 text-green-500 rounded-full flex items-center justify-center mx-auto mb-6"><CheckCircle2 size={32} /></div>
          <h2 className="text-2xl font-bold text-slate-100 mb-2">Check your email</h2>
          <p className="text-slate-400 mb-6">We&apos;ve sent a magic link to <span className="text-slate-200 font-medium">{email}</span>.<br />Click it in this same browser to {mode === 'signup' ? 'create your account' : 'log in'}.</p>
          <button onClick={() => setSent(false)} className="text-sm font-medium hover:underline" style={{ color: '#FF3B30' }}>Try a different email</button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center p-4 sm:p-6 relative overflow-hidden w-full min-h-0">
      <div className="max-w-md w-full backdrop-blur-xl border rounded-3xl p-6 sm:p-8 shadow-2xl z-10 relative" style={{ backgroundColor: 'rgba(47,47,47,0.9)', borderColor: '#3d3d3d' }}>
        {onClose && <button onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-white transition-colors"><X size={24} /></button>}

        <div className="text-center mb-6">
          <div className="flex justify-center mb-4">
            <img src="/Planos Logo Red.png" alt="Planos.studio" className="h-16 w-auto object-contain" />
          </div>
          {mode === 'signup' && (
            <div className="inline-flex items-center gap-2 px-3 py-2 rounded-xl mb-4 border" style={{ backgroundColor: 'rgba(255,59,48,0.15)', borderColor: 'rgba(255,59,48,0.3)' }}>
              <Coins size={18} style={{ color: '#FF3B30' }} />
              <span className="text-sm font-semibold" style={{ color: '#FF3B30' }}>10 Free Credits</span>
            </div>
          )}
          <h1 className="text-3xl font-bold text-slate-100 mb-2">{mode === 'signup' ? 'Create Account' : 'Welcome Back'}</h1>
          <p className="text-slate-400">{mode === 'signup' ? `Sign up to get 10 free credits and start creating with ${APP_NAME}! No credit card required.` : `Sign in to access your ${APP_NAME} projects.`}</p>
        </div>

        <div className="mb-6 space-y-3 text-left p-3 rounded-xl border" style={{ backgroundColor: 'rgba(0,0,0,0.3)', borderColor: '#3d3d3d' }}>
          <label className="flex items-start gap-3 cursor-pointer group">
            <div className="relative flex items-center pt-0.5">
              <input type="checkbox" checked={acceptedTerms} onChange={(e) => setAcceptedTerms(e.target.checked)} className="peer h-4 w-4 shrink-0 rounded border-slate-600 bg-slate-900 appearance-none border transition-all" />
              {acceptedTerms && <Check className="absolute top-0.5 left-0.5 h-3 w-3 text-white pointer-events-none" strokeWidth={3.5} />}
            </div>
            <span className="text-xs text-slate-400 group-hover:text-slate-300 select-none leading-relaxed">
              I accept the <a href="https://rehub.studio/terms" target="_blank" rel="noopener noreferrer" className="font-medium hover:underline" style={{ color: '#FF3B30' }}>Terms and Conditions</a> and <a href="https://rehub.studio/privacy" target="_blank" rel="noopener noreferrer" className="font-medium hover:underline" style={{ color: '#FF3B30' }}>Privacy Policy</a>.
            </span>
          </label>
          {mode === 'signup' && (
            <label className="flex items-start gap-3 cursor-pointer group">
              <div className="relative flex items-center pt-0.5">
                <input type="checkbox" checked={acceptedMarketing} onChange={(e) => setAcceptedMarketing(e.target.checked)} className="peer h-4 w-4 shrink-0 rounded border-slate-600 bg-slate-900 appearance-none border transition-all" />
                {acceptedMarketing && <Check className="absolute top-0.5 left-0.5 h-3 w-3 text-white pointer-events-none" strokeWidth={3.5} />}
              </div>
              <span className="text-xs text-slate-400 group-hover:text-slate-300 select-none leading-relaxed">I agree to receive marketing, offers, and updates. <span className="text-slate-500 text-[10px]">(Optional)</span></span>
            </label>
          )}
        </div>

        {error && <div className="flex items-start gap-2 p-3 bg-red-900/20 border border-red-900/50 rounded-lg text-red-400 text-sm mb-6"><AlertCircle size={16} className="shrink-0 mt-0.5" /><span>{error}</span></div>}

        <form onSubmit={handleAuth} className="space-y-4">
          <div className="absolute -left-[9999px] top-0 h-0 w-0 overflow-hidden" aria-hidden="true"><label htmlFor="website">Website</label><input type="text" id="website" name="website" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} /></div>
          <div className="space-y-2">
            <label htmlFor="email" className="block text-sm font-medium text-slate-300">Email Address</label>
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-500 group-focus-within:text-[#FF3B30] transition-colors"><Mail size={18} /></div>
              <input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="block w-full pl-10 pr-3 py-3 bg-black border border-slate-700 rounded-xl text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-[#FF3B30]/50 focus:border-[#FF3B30] transition-all" />
            </div>
          </div>
          <button type="submit" disabled={loading || googleLoading} className="w-full flex items-center justify-center gap-2 py-3.5 px-4 text-white rounded-xl font-semibold transition-all shadow-lg disabled:opacity-50 group hover:opacity-90" style={{ backgroundColor: '#FF3B30', boxShadow: '0 8px 30px -8px rgba(255,59,48,0.35)' }}>
            {loading ? <Loader2 size={20} className="animate-spin" /> : <>{mode === 'signup' ? 'Create Account' : 'Sign In'}<ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" /></>}
          </button>
        </form>

        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center"><div className="w-full border-t" style={{ borderColor: '#3d3d3d' }}></div></div>
          <div className="relative flex justify-center text-sm"><span className="px-3 text-slate-500" style={{ backgroundColor: 'rgba(47,47,47,0.9)' }}>or continue with</span></div>
        </div>
        <button
          type="button"
          onClick={handleGoogleSignIn}
          disabled={loading || googleLoading}
          className="w-full flex items-center justify-center gap-3 py-3.5 px-4 bg-white hover:bg-gray-100 text-gray-800 rounded-xl font-semibold transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {googleLoading ? (
            <Loader2 size={20} className="animate-spin text-gray-600" />
          ) : (
            <>
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Continue with Google
            </>
          )}
        </button>

        <div className="mt-6 pt-6 border-t text-center" style={{ borderColor: '#3d3d3d' }}>
          <p className="text-slate-400 text-sm">{mode === 'signup' ? 'Already have an account?' : "Don't have an account?"}</p>
          <button onClick={toggleMode} className="font-medium text-sm mt-1 hover:underline" style={{ color: '#FF3B30' }}>{mode === 'signup' ? 'Log in here' : 'Sign up for free'}</button>
        </div>

        {/* Pricing: credit hosting + active space plans (shared with 360 Tours) */}
        <div className="mt-6 pt-6 border-t rounded-xl p-4 space-y-4" style={{ borderColor: '#3d3d3d', backgroundColor: 'rgba(0,0,0,0.2)' }}>
          <p className="text-center text-slate-400 text-sm font-medium">Simple, transparent pricing</p>
          <div className="space-y-2">
            {HOSTING_PLANS.filter((p) => p.plan !== 'slot').sort((a, b) => a.days - b.days).map((plan) => (
              <div key={plan.plan} className="flex items-center justify-between py-2 px-3 rounded-lg border border-slate-700/60" style={{ backgroundColor: 'rgba(47,47,47,0.6)' }}>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: '#FF3B30' }} />
                  <span className="text-slate-200 font-medium text-sm">{plan.label}</span>
                  {plan.badge === 'free' && (
                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border" style={{ backgroundColor: 'rgba(34,197,94,0.2)', color: '#22c55e', borderColor: 'rgba(34,197,94,0.4)' }}>FREE WITH SIGNUP</span>
                  )}
                </div>
                <span className="text-sm font-semibold tabular-nums" style={{ color: '#FF3B30' }}>
                  {plan.credits} {plan.credits === 1 ? 'Credit' : 'Credits'}
                </span>
              </div>
            ))}
          </div>
          <p className="text-slate-400 text-xs font-semibold uppercase tracking-wide text-center">Active space plans (360 Tours + Planos)</p>
          <p className="text-center text-slate-400 text-xs">Subscribe for active spaces — publish without spending credits per space.</p>
          <div className="space-y-2">
            {TOUR_SUBSCRIPTION_TIERS.filter((t) => t.tier !== 'free').map((tier) => (
              <div key={tier.tier} className="flex items-center justify-between py-2 px-3 rounded-lg border border-slate-700/60" style={{ backgroundColor: 'rgba(47,47,47,0.6)' }}>
                <span className="text-slate-200 font-medium text-sm">{tier.name}</span>
                <span className="text-slate-300 text-xs tabular-nums">
                  £{tier.monthlyPriceGBP}/mo · {tier.activeSlotLimit} spaces{tier.bonusCreditsPerMonth > 0 ? ` · ${tier.bonusCreditsPerMonth} credits/mo` : ''}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
